import React, { useState, useEffect, useCallback } from 'react';
import { Routes, Route } from 'react-router-dom';
import { FiEdit2 } from 'react-icons/fi';
import api from '../services/api';
import WhatsappConfig from '../components/Maintenance/Config/WhatsappConfig';
import '../styles/Configuracion.css';

/* ══════════════════════════════════════════════════════════════════════════
   GENERIC CONFIG TABLE
   Columns: [{ key, label }]
   Fields:  [{ key, label, type:'text'|'select', options:[{value,label}] }]
   apiPath: e.g. '/api/config/tiendas'
══════════════════════════════════════════════════════════════════════════ */
function ConfigTable({ title, columns, fields, apiPath, showSearch = true, businessId }) {
  const [rows, setRows]       = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch]   = useState('');
  const [page, setPage]       = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [modal, setModal]     = useState(null); // null | { mode:'new'|'edit', data:{} }
  const [form, setForm]       = useState({});
  const [saving, setSaving]   = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [modalError, setModalError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get(`${apiPath}?business_id=${businessId}`);
      const data = res.data.data || [];
      setRows(data);
      setFiltered(data);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, [apiPath]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!search.trim()) { setFiltered(rows); return; }
    const q = search.toLowerCase();
    setFiltered(rows.filter(r => columns.some(c => String(r[c.key] || '').toLowerCase().includes(q))));
    setPage(1);
  }, [search, rows, columns]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const pageRows   = filtered.slice((page - 1) * perPage, page * perPage);

  const openNew  = () => {
    const blank = {};
    fields.forEach(f => { blank[f.key] = ''; });
    setForm(blank);
    setModalError('');
    setModal({ mode: 'new' });
  };
  const openEdit = (row) => {
    const data = {};
    fields.forEach(f => { data[f.key] = row[f.key] ?? ''; });
    setForm(data);
    setModalError('');
    setModal({ mode: 'edit', id: row.id });
  };
  const closeModal = () => { setModal(null); setModalError(''); };

  const handleSave = async () => {
    setSaving(true);
    setModalError('');
    try {
      if (modal.mode === 'new') {
        await api.post(`${apiPath}?business_id=${businessId}`, form);
      } else {
        await api.put(`${apiPath}/${modal.id}?business_id=${businessId}`, form);
      }
      await load();
      closeModal();
    } catch (e) {
      const msg = e.response?.data?.error || e.message || 'Error al guardar';
      const isDup = msg.includes('Duplicate') || msg.includes('ER_DUP');
      setModalError(isDup ? 'Ya existe un registro con ese nombre.' : msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="cfg-page">
      {/* ── TOOLBAR ── */}
      <div className="cfg-toolbar">
        <button className="cfg-btn-nuevo" onClick={openNew}>
          <FiEdit2 /> Nuevo
        </button>
        {showSearch && (
          <div className="cfg-search-wrap">
            <span className="cfg-search-label">Busqueda Global</span>
            <input
              className="cfg-search"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Buscar..."
            />
          </div>
        )}
      </div>

      {/* ── PAGINATION TOP ── */}
      <Pagination page={page} total={totalPages} count={filtered.length} perPage={perPage}
        onPage={setPage} onPerPage={v => { setPerPage(v); setPage(1); }} />

      {/* ── TABLE ── */}
      {loading ? (
        <div className="cfg-loading">Cargando...</div>
      ) : error ? (
        <div className="cfg-error">{error}</div>
      ) : (
        <table className="cfg-table">
          <thead>
            <tr>
              {columns.map(c => (
                <th key={c.key} className="cfg-th">{c.label}</th>
              ))}
              <th className="cfg-th cfg-th-action"></th>
            </tr>
            <tr className="cfg-filter-row">
              {columns.map(c => (
                <td key={c.key}>
                  <input className="cfg-col-filter" placeholder="" readOnly />
                </td>
              ))}
              <td></td>
            </tr>
          </thead>
          <tbody>
            {pageRows.length === 0 ? (
              <tr><td colSpan={columns.length + 1} className="cfg-empty">Sin datos</td></tr>
            ) : pageRows.map((row, i) => (
              <tr key={row.id} className={i % 2 === 0 ? 'cfg-row-even' : 'cfg-row-odd'}>
                {columns.map(c => (
                  <td key={c.key} className="cfg-td">{row[c.key] ?? ''}</td>
                ))}
                <td className="cfg-td cfg-td-action">
                  <button className="cfg-btn-edit" onClick={() => openEdit(row)} title="Editar">
                    <FiEdit2 />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* ── PAGINATION BOTTOM ── */}
      <Pagination page={page} total={totalPages} count={filtered.length} perPage={perPage}
        onPage={setPage} onPerPage={v => { setPerPage(v); setPage(1); }} />

      <div className="cfg-footer">
        Todos los Derechos reservados Devel@per F@xy &nbsp;·&nbsp; www.developerfoxy.com
      </div>

      {/* ── MODAL ── */}
      {modal && (
        <div className="cfg-modal-overlay">
          <div className="cfg-modal" onClick={e => e.stopPropagation()}>
            <div className="cfg-modal-header">
              <span>Mantenimiento de {title}</span>
              <button className="cfg-modal-close" onClick={closeModal}>×</button>
            </div>
            <div className="cfg-modal-body">
              {fields.map(f => (
                <div className="cfg-field-row" key={f.key}>
                  <label className="cfg-field-label">{f.label}</label>
                  {f.type === 'select' ? (
                    <select
                      className="cfg-input"
                      value={form[f.key] || ''}
                      onChange={e => setForm({ ...form, [f.key]: e.target.value })}
                    >
                      <option value="">-- Seleccionar --</option>
                      {(f.options || []).map(o => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      className="cfg-input"
                      value={form[f.key] || ''}
                      onChange={e => setForm({ ...form, [f.key]: e.target.value })}
                    />
                  )}
                </div>
              ))}
            </div>
            {modalError && (
              <div className="cfg-modal-error">{modalError}</div>
            )}
            <div className="cfg-modal-footer">
              <button className="cfg-btn-save" onClick={handleSave} disabled={saving}>
                {saving ? 'Guardando...' : modal?.mode === 'new' ? 'Guardar' : 'Modificar'}
              </button>
              <button className="cfg-btn-cancel" onClick={closeModal}>× Salir</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Pagination component ─────────────────────────────────────────────── */
function Pagination({ page, total, count, perPage, onPage, onPerPage }) {
  return (
    <div className="cfg-pagination">
      <span className="cfg-page-info">({page} of {total})</span>
      <button onClick={() => onPage(1)}       disabled={page === 1}>|&lt;</button>
      <button onClick={() => onPage(page - 1)} disabled={page === 1}>&lt;&lt;</button>
      <span className="cfg-page-num">{page}</span>
      <button onClick={() => onPage(page + 1)} disabled={page === total}>&gt;&gt;</button>
      <button onClick={() => onPage(total)}   disabled={page === total}>&gt;|</button>
      <select value={perPage} onChange={e => onPerPage(Number(e.target.value))}>
        {[5,10,20,50].map(n => <option key={n} value={n}>{n}</option>)}
      </select>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   SUB-MÓDULOS
══════════════════════════════════════════════════════════════════════════ */

/* ── Serie Venta especial: el selector de Documento Pago es dinámico ─── */
function SerieVentaModule({ businessId }) {
  const [payDocs, setPayDocs] = useState([]);
  useEffect(() => {
    api.get(`/api/config/docs-pago?business_id=${businessId}`)
      .then(r => setPayDocs(r.data.data || []))
      .catch(() => {});
  }, [businessId]);

  const columns = [
    { key: 'doc_name', label: 'Doc. Compra Venta' },
    { key: 'serie',    label: 'Serie' },
    { key: 'sequence', label: 'Secuencia' },
  ];
  const fields = [
    {
      key: 'payment_document_id', label: 'Ingrese Documento Pago',
      type: 'select',
      options: payDocs.map(d => ({ value: d.id, label: d.name })),
    },
    { key: 'serie',    label: 'Serie' },
    { key: 'sequence', label: 'Secuencia' },
  ];

  return (
    <ConfigTable
      title="Series Venta"
      columns={columns}
      fields={fields}
      apiPath="/api/config/series-venta"
      showSearch={true}
      businessId={businessId}
    />
  );
}

/* ── Tipos de Movimiento — módulo con applies_to especial ───────────── */
export function TiposMovimientoModule({ businessId }) {
  const APPLIES_LABELS = { ingreso: 'INGRESO', salida: 'SALIDA', ambos: 'AMBOS' };
  const APPLIES_COLORS = { ingreso: '#16a34a', salida: '#dc2626', ambos: '#d97706' };

  const columns = [
    { key: 'name',       label: 'Tipo de Movimiento' },
    {
      key: 'applies_to', label: 'Aplica a',
      render: v => (
        <span style={{
          background: APPLIES_COLORS[v] + '18',
          color: APPLIES_COLORS[v],
          border: `1px solid ${APPLIES_COLORS[v]}44`,
          borderRadius: 12, padding: '2px 10px',
          fontWeight: 700, fontSize: 11
        }}>
          {APPLIES_LABELS[v] || v}
        </span>
      )
    },
  ];
  const fields = [
    { key: 'name', label: 'Nombre del Tipo' },
    {
      key: 'applies_to', label: 'Aplica a',
      type: 'select',
      options: [
        { value: 'ingreso', label: 'INGRESO' },
        { value: 'salida',  label: 'SALIDA' },
        { value: 'ambos',   label: 'AMBOS (Ingreso y Salida)' },
      ]
    },
  ];

  return (
    <ConfigTableWithRender
      title="Tipo Movimiento"
      columns={columns}
      fields={fields}
      apiPath="/api/config/tipos-movimiento"
      businessId={businessId}
    />
  );
}

/* ── ConfigTableWithRender — igual que ConfigTable pero soporta render() ─ */
function ConfigTableWithRender({ title, columns, fields, apiPath, businessId }) {
  const [rows, setRows]       = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch]   = useState('');
  const [page, setPage]       = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [modal, setModal]     = useState(null);
  const [form, setForm]       = useState({});
  const [saving, setSaving]   = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [modalError, setModalError] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = await api.get(`${apiPath}?business_id=${businessId}`);
      const data = res.data.data || [];
      setRows(data); setFiltered(data);
    } catch (e) { setError(e.response?.data?.error || e.message); }
    finally { setLoading(false); }
  }, [apiPath, businessId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!search.trim()) { setFiltered(rows); return; }
    const q = search.toLowerCase();
    setFiltered(rows.filter(r => columns.some(c => String(r[c.key] || '').toLowerCase().includes(q))));
    setPage(1);
  }, [search, rows, columns]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const pageRows   = filtered.slice((page - 1) * perPage, page * perPage);

  const openNew  = () => {
    const blank = {}; fields.forEach(f => { blank[f.key] = f.type === 'select' ? (f.options?.[0]?.value || '') : ''; });
    setForm(blank); setModalError(''); setModal({ mode: 'new' });
  };
  const openEdit = (row) => {
    const data = {}; fields.forEach(f => { data[f.key] = row[f.key] ?? ''; });
    setForm(data); setModalError(''); setModal({ mode: 'edit', id: row.id });
  };

  const handleSave = async () => {
    setSaving(true); setModalError('');
    try {
      if (modal.mode === 'new') await api.post(`${apiPath}?business_id=${businessId}`, form);
      else await api.put(`${apiPath}/${modal.id}?business_id=${businessId}`, form);
      await load(); setModal(null); setModalError('');
    } catch (e) {
      const msg = e.response?.data?.error || e.message || 'Error al guardar';
      const isDup = msg.includes('Duplicate') || msg.includes('ER_DUP');
      setModalError(isDup ? 'Ya existe un registro con ese nombre.' : msg);
    } finally { setSaving(false); }
  };

  return (
    <div className="cfg-page">
      <div className="cfg-toolbar">
        <button className="cfg-btn-nuevo" onClick={openNew}><FiEdit2 /> Nuevo</button>
        <div className="cfg-search-wrap">
          <span className="cfg-search-label">Busqueda Global</span>
          <input className="cfg-search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar..." />
        </div>
      </div>
      <Pagination page={page} total={totalPages} count={filtered.length} perPage={perPage}
        onPage={setPage} onPerPage={v => { setPerPage(v); setPage(1); }} />
      {loading ? <div className="cfg-loading">Cargando...</div> : error ? <div className="cfg-error">{error}</div> : (
        <table className="cfg-table">
          <thead>
            <tr>{columns.map(c => <th key={c.key} className="cfg-th">{c.label}</th>)}<th className="cfg-th cfg-th-action"></th></tr>
            <tr className="cfg-filter-row">
              {columns.map(c => <td key={c.key}><input className="cfg-col-filter" readOnly /></td>)}
              <td></td>
            </tr>
          </thead>
          <tbody>
            {pageRows.length === 0
              ? <tr><td colSpan={columns.length + 1} className="cfg-empty">Sin datos</td></tr>
              : pageRows.map((row, i) => (
                <tr key={row.id} className={i % 2 === 0 ? 'cfg-row-even' : 'cfg-row-odd'}>
                  {columns.map(c => (
                    <td key={c.key} className="cfg-td">
                      {c.render ? c.render(row[c.key]) : (row[c.key] ?? '')}
                    </td>
                  ))}
                  <td className="cfg-td cfg-td-action">
                    <button className="cfg-btn-edit" onClick={() => openEdit(row)}><FiEdit2 /></button>
                  </td>
                </tr>
              ))
            }
          </tbody>
        </table>
      )}
      <Pagination page={page} total={totalPages} count={filtered.length} perPage={perPage}
        onPage={setPage} onPerPage={v => { setPerPage(v); setPage(1); }} />
      <div className="cfg-footer">Todos los Derechos reservados Devel@per F@xy &nbsp;·&nbsp; www.developerfoxy.com</div>
      {modal && (
        <div className="cfg-modal-overlay">
          <div className="cfg-modal" onClick={e => e.stopPropagation()}>
            <div className="cfg-modal-header">
              <span>Mantenimiento de {title}</span>
              <button className="cfg-modal-close" onClick={() => setModal(null)}>×</button>
            </div>
            <div className="cfg-modal-body">
              {fields.map(f => (
                <div className="cfg-field-row" key={f.key}>
                  <label className="cfg-field-label">{f.label}</label>
                  {f.type === 'select' ? (
                    <select className="cfg-input" value={form[f.key] || ''} onChange={e => setForm({ ...form, [f.key]: e.target.value })}>
                      {(f.options || []).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  ) : (
                    <input className="cfg-input" value={form[f.key] || ''} onChange={e => setForm({ ...form, [f.key]: e.target.value })} />
                  )}
                </div>
              ))}
              {modalError && <div className="cfg-modal-error">{modalError}</div>}
            </div>
            <div className="cfg-modal-footer">
              <button className="cfg-btn-save" onClick={handleSave} disabled={saving}>
                {saving ? 'Guardando...' : modal?.mode === 'new' ? 'Guardar' : 'Modificar'}
              </button>
              <button className="cfg-btn-cancel" onClick={() => { setModal(null); setModalError(''); }}>× Salir</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Módulos simples ────────────────────────────────────────────────── */
export function UnidadesModule({ businessId }) {
  return (
    <ConfigTable
      title="Unidad de Medida"
      columns={[
        { key: 'short_name', label: 'Descripcion Corta' },
        { key: 'name',       label: 'Descripcion' },
      ]}
      fields={[
        { key: 'short_name', label: 'Descripcion Corta' },
        { key: 'name',       label: 'Descripcion' },
      ]}
      apiPath="/api/config/unidades"
      showSearch={false}
      businessId={businessId}
    />
  );
}

export function FormasPagoModule({ businessId }) {
  return (
    <ConfigTable
      title="Forma Pago"
      columns={[
        { key: 'name',       label: 'Forma de Pago' },
        { key: 'short_name', label: 'Nombre Corto' },
      ]}
      fields={[
        { key: 'name',       label: 'Forma de Pago' },
        { key: 'short_name', label: 'Nombre Corto' },
      ]}
      apiPath="/api/config/formas-pago"
      showSearch={false}
      businessId={businessId}
    />
  );
}

export function TiposDocumentoModule({ businessId }) {
  return (
    <ConfigTable
      title="Tipo Documento"
      columns={[
        { key: 'name',       label: 'Tipo Documento' },
        { key: 'short_name', label: 'Nombre Corto' },
      ]}
      fields={[
        { key: 'name',       label: 'Tipo Documento' },
        { key: 'short_name', label: 'Nombre Corto' },
      ]}
      apiPath="/api/config/tipos-documento"
      showSearch={false}
      businessId={businessId}
    />
  );
}

export function DocsPagoModule({ businessId }) {
  return (
    <ConfigTable
      title="Documentos Pago"
      columns={[
        { key: 'name',       label: 'Doc. Compra Venta' },
        { key: 'short_name', label: 'Nombre Corto' },
      ]}
      fields={[
        { key: 'name',       label: 'Doc. Compra Venta' },
        { key: 'short_name', label: 'Nombre Corto' },
      ]}
      apiPath="/api/config/docs-pago"
      businessId={businessId}
    />
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   ROUTER
══════════════════════════════════════════════════════════════════════════ */
function ConfiguracionPage({ businessId }) {
  return (
    <Routes>
      <Route path="unidades"         element={<UnidadesModule        businessId={businessId} />} />
      <Route path="formas-pago"      element={<FormasPagoModule      businessId={businessId} />} />
      <Route path="tipos-documento"  element={<TiposDocumentoModule  businessId={businessId} />} />
      <Route path="docs-pago"        element={<DocsPagoModule        businessId={businessId} />} />
      <Route path="series-venta"      element={<SerieVentaModule         businessId={businessId} />} />
      <Route path="tipos-movimiento"  element={<TiposMovimientoModule    businessId={businessId} />} />
      <Route path="whatsapp"          element={<WhatsappConfig />} />
      <Route path="*"                 element={<UnidadesModule           businessId={businessId} />} />
    </Routes>
  );
}

export default ConfiguracionPage;
